import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  saApps,
  saAppPlatformMeta,
  saAppStatusMeta,
  totalDownloads,
  type SAAppPlatform,
  type SAApp,
} from '@/mocks/saApps';

const allPlatforms: SAAppPlatform[] = [
  'android',
  'iphone',
  'android-tv',
  'fire-tv',
  'samsung-tv',
  'lg-tv',
];

function formatDownloads(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return String(n);
}

type Step = 'select' | 'detail';

export default function DownloadAppPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [step, setStep] = useState<Step>('select');
  const [selectedApp, setSelectedApp] = useState<SAApp | null>(null);

  const openModal = () => {
    setStep('select');
    setSelectedApp(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setTimeout(() => { setStep('select'); setSelectedApp(null); }, 200);
  };

  const selectPlatform = (platform: SAAppPlatform) => {
    const app = saApps.find((a) => a.platform === platform) || null;
    setSelectedApp(app);
    setStep('detail');
  };

  const backToSelect = () => {
    setStep('select');
    setSelectedApp(null);
  };

  return (
    <div className="min-h-screen bg-background-50">
      {/* Simple nav */}
      <nav className="flex items-center justify-between px-4 md:px-8 h-16 border-b border-background-200 bg-background-50/98 backdrop-blur-2xl sticky top-0 z-30">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer">
          <div className="w-8 h-8 rounded-lg bg-foreground-950 flex items-center justify-center">
            <i className="ri-radio-line text-background-50 text-sm"></i>
          </div>
          <span className="text-foreground-950 font-bold text-sm">RadioFrecuenciaWeb</span>
        </Link>
        <Link to="/" className="text-foreground-400 text-xs hover:text-foreground-600 transition-colors cursor-pointer">
          <i className="ri-arrow-left-line mr-1"></i>
          Inicio
        </Link>
      </nav>

      {/* HERO */}
      <section className="relative px-4 py-16 md:py-24 lg:py-32">
        {/* Background image */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Abstract%20geometric%20waves%20and%20circles%20in%20warm%20amber%20and%20emerald%20tones%2C%20soft%20gradient%20overlay%2C%20minimalist%20tech%20background%20with%20subtle%20radiating%20lines%2C%20smooth%20organic%20shapes%2C%20modern%20digital%20art%20style%2C%20clean%20composition%2C%20warm%20earthy%20accent%20colors%2C%20professional%20streaming%20platform%20aesthetic&width=1600&height=900&seq=sa39-hero-bg&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-background-50/85"></div>
        </div>

        <div className="relative max-w-2xl mx-auto text-center">
          <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-100 text-amber-700 text-[11px] font-semibold mb-6">
            <i className="ri-download-2-line text-sm"></i>
            {formatDownloads(totalDownloads)}+ descargas
          </div>
          <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-950 mb-4 leading-tight">
            Llevá la radio<br />a todas partes
          </h1>
          <p className="text-foreground-500 text-sm md:text-base max-w-lg mx-auto mb-8 leading-relaxed">
            Descargá RadioFrecuenciaWeb en tu dispositivo favorito. Disponible en Android, iPhone, Smart TVs y más. Escuchá tus radios donde quieras, cuando quieras.
          </p>

          <button
            onClick={openModal}
            className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold hover:bg-foreground-800 transition-all cursor-pointer whitespace-nowrap shadow-lg shadow-foreground-950/10"
          >
            <i className="ri-download-2-line text-lg"></i>
            Descargar App
          </button>
        </div>
      </section>

      {/* Features strip */}
      <section className="px-4 md:px-8 pb-16 md:pb-24">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {[
              { icon: 'ri-smartphone-line', label: 'Móvil', desc: 'Android y iPhone' },
              { icon: 'ri-tv-line', label: 'Smart TV', desc: '4 plataformas' },
              { icon: 'ri-live-line', label: 'En vivo', desc: 'Streaming HD' },
              { icon: 'ri-shield-check-line', label: 'Segura', desc: 'Sin anuncios' },
            ].map((item) => (
              <div key={item.label} className="text-center p-4 rounded-2xl bg-background-100 border border-background-200">
                <div className="w-10 h-10 mx-auto rounded-xl bg-amber-100 flex items-center justify-center mb-3">
                  <i className={`${item.icon} text-amber-600 text-lg`}></i>
                </div>
                <p className="text-foreground-800 text-xs font-bold mb-0.5">{item.label}</p>
                <p className="text-foreground-400 text-[10px]">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-background-200 py-6 px-4 text-center">
        <p className="text-foreground-400 text-[11px]">
          RadioFrecuenciaWeb &copy; 2026 · Disponible en múltiples plataformas
        </p>
      </footer>

      {/* DOWNLOAD MODAL */}
      {modalOpen && (
        <div
          className="fixed inset-0 z-[70] bg-black/40 flex items-center justify-center p-4"
          onClick={closeModal}
        >
          <div
            className="bg-background-50 rounded-2xl border border-background-200 w-full max-w-md overflow-hidden max-h-[90vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-background-200 shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center">
                  <i className="ri-download-2-line text-amber-600 text-base"></i>
                </div>
                <div>
                  <h3 className="text-foreground-950 font-bold text-sm">
                    {step === 'select' ? 'Descargar App' : saAppPlatformMeta[selectedApp!.platform].label}
                  </h3>
                  <p className="text-foreground-400 text-[10px]">
                    {step === 'select' ? 'Elegí tu dispositivo' : `v${selectedApp!.version}`}
                  </p>
                </div>
              </div>
              <button
                onClick={closeModal}
                className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center cursor-pointer hover:bg-background-200 transition-colors shrink-0"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto">
              {step === 'select' ? (
                /* STEP 1 — PLATFORM SELECTOR */
                <div className="p-5">
                  <p className="text-foreground-500 text-xs mb-4">¿Qué dispositivo usas?</p>
                  <div className="grid grid-cols-2 gap-2">
                    {allPlatforms.map((p) => {
                      const meta = saAppPlatformMeta[p];
                      const app = saApps.find((a) => a.platform === p);
                      const statusMeta = app ? saAppStatusMeta[app.status] : null;
                      return (
                        <button
                          key={p}
                          onClick={() => selectPlatform(p)}
                          className="flex flex-col items-center gap-2 p-4 rounded-xl bg-background-100 border border-background-200 hover:border-amber-300 hover:bg-amber-50/50 transition-all cursor-pointer text-center group"
                        >
                          <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center group-hover:bg-amber-200 transition-colors">
                            <i className={`${meta.icon} text-amber-600 text-lg`}></i>
                          </div>
                          <div>
                            <p className="text-foreground-800 text-xs font-bold">{meta.label}</p>
                            <p className="text-foreground-400 text-[9px]">{meta.storeName}</p>
                          </div>
                          {app && (
                            <span className={`inline-flex px-1.5 py-0.5 rounded-md text-[8px] font-semibold ${statusMeta!.bgColor} ${statusMeta!.color}`}>
                              {statusMeta!.label}
                            </span>
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                /* STEP 2 — APP DETAIL */
                selectedApp && (
                  <div className="p-5">
                    {/* Platform header */}
                    <div className="flex items-center gap-3 mb-5">
                      <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center shrink-0">
                        <i className={`${saAppPlatformMeta[selectedApp.platform].icon} text-amber-600 text-xl`}></i>
                      </div>
                      <div>
                        <div className="flex items-center gap-2 mb-0.5">
                          <h4 className="text-foreground-950 font-bold text-sm">
                            {saAppPlatformMeta[selectedApp.platform].label}
                          </h4>
                          <span className={`inline-flex px-1.5 py-0.5 rounded-md text-[9px] font-semibold ${saAppStatusMeta[selectedApp.status].bgColor} ${saAppStatusMeta[selectedApp.status].color}`}>
                            {saAppStatusMeta[selectedApp.status].label}
                          </span>
                        </div>
                        <p className="text-foreground-400 text-[10px]">
                          {saAppPlatformMeta[selectedApp.platform].storeName} · v{selectedApp.version}
                        </p>
                      </div>
                    </div>

                    {/* QR */}
                    <div className="flex justify-center mb-5">
                      <div className="w-36 h-36 rounded-2xl bg-background-100 border border-background-200 flex items-center justify-center">
                        <div className="text-center">
                          <i className="ri-qr-code-line text-foreground-300 text-5xl"></i>
                          <p className="text-foreground-400 text-[8px] mt-1">QR Code</p>
                        </div>
                      </div>
                    </div>

                    {/* Details */}
                    <div className="bg-background-100 rounded-xl p-3 space-y-2 mb-5">
                      <div className="flex justify-between text-[11px]">
                        <span className="text-foreground-400">Versión</span>
                        <span className="text-foreground-800 font-mono font-semibold">v{selectedApp.version}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-foreground-400">Tamaño</span>
                        <span className="text-foreground-800">{selectedApp.size}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-foreground-400">OS mínimo</span>
                        <span className="text-foreground-800">{selectedApp.minOsVersion}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-foreground-400">Descargas</span>
                        <span className="text-foreground-800 font-semibold">{formatDownloads(selectedApp.downloads)}</span>
                      </div>
                      <div className="flex justify-between text-[11px]">
                        <span className="text-foreground-400">Última actualización</span>
                        <span className="text-foreground-800">{new Date(selectedApp.lastUpdated + 'T00:00:00').toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}</span>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2">
                      <button
                        onClick={backToSelect}
                        className="flex items-center gap-1 px-4 py-2.5 rounded-xl bg-background-100 text-foreground-500 text-xs font-semibold hover:bg-background-200 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-arrow-left-line text-sm"></i>
                        Otro dispositivo
                      </button>
                      <a
                        href={selectedApp.downloadUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl bg-foreground-950 text-background-50 text-xs font-bold hover:bg-foreground-800 transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className="ri-download-2-line text-sm"></i>
                        Ir a {saAppPlatformMeta[selectedApp.platform].storeName.split(' ')[0]}
                      </a>
                    </div>
                  </div>
                )
              )}
            </div>
          </div>
        </div>
      )}

      {/* Mobile bottom */}
      <div className="md:hidden fixed bottom-0 inset-x-0 z-30 bg-background-50/98 backdrop-blur-2xl border-t border-background-200 py-3 px-4 safe-bottom">
        <button
          onClick={openModal}
          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-foreground-950 text-background-50 text-sm font-bold cursor-pointer whitespace-nowrap"
        >
          <i className="ri-download-2-line text-lg"></i>
          Descargar App
        </button>
      </div>
    </div>
  );
}